import { InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import useAuth from '~/hooks/useAuth';
import { BasicAccountInfo } from '~/types/account.types';
import axios from '~/utils/axios';

interface AccountsResponse {
  accounts: BasicAccountInfo[];
  hasMore: boolean;
  total: number;
}

/**
 * Hook to fetch user accounts with pagination
 * @param pageSize - Number of accounts to fetch per page
 * @returns InfiniteQuery result with accounts data
 */
export const useAccounts = (pageSize: number = 15) => {
  const { isLoggedIn } = useAuth();

  return useInfiniteQuery<
    AccountsResponse,
    Error,
    InfiniteData<AccountsResponse>,
    (string | number)[],
    number
  >({
    queryKey: ['accounts', pageSize],
    initialPageParam: 0,
    queryFn: async ({ pageParam = 0 }) => {
      const response = await axios.get('/me/accounts/basic', {
        params: {
          offset: pageParam,
          limit: pageSize,
          sortBy: 'createdAt',
          sortOrder: 'desc',
        },
      });
      return response.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.hasMore) {
        // Calculate the next offset based on the current pages
        return allPages.reduce((acc, page) => acc + page.accounts.length, 0);
      }
      return undefined;
    },
    enabled: isLoggedIn,
    staleTime: 60000, // 1 minute cache TTL
    gcTime: 600000, // 10 minutes - param change with react query v5
    refetchOnWindowFocus: false,
  });
};
